/**
 * structuredPackaging.ts
 *
 * Frontend service and utilities for structured packaging BOM entry,
 * validation, legacy conversions, billable qty calculations, and spec formatting.
 */

import {
  GIAY_L_OPTIONS,
} from '../features/boms/packagingPriceCalc'

export function parseN(v: unknown): number {
  if (v === null || v === undefined) return 0
  if (typeof v === 'number') return isNaN(v) ? 0 : v
  const s = String(v).replace(/,/g, '').trim()
  const m = s.match(/^-?\d+(\.\d+)?/)
  return m ? parseFloat(m[0]) : 0
}

export function tinhDT_Carton(shape: string, D: number, R: number, C: number): number {
  const norm = (shape || '').trim().toLowerCase()
  if (norm.includes('âm dương') || norm.includes('am duong')) {
    return (2 * (D + 2 * C + 30) * (R + 2 * C + 30)) / 1e6
  }
  if (norm.includes('a5 3 cao') || norm.includes('3 cao')) {
    return ((D + 2 * C + 30) * (2 * R + 3 * C)) / 1e6
  }
  if (norm.includes('a5')) {
    return ((D + 2 * C + 30) * (2 * R + 2 * C + 50)) / 1e6
  }
  // Default: Thùng A1
  return ((D + R + 40) * 2 * (R + C + 30)) / 1e6
}

export type PackagingTypeCode =
  | 'thung'
  | 'xop_tam'
  | 'xop_l'
  | 'xop_goc'
  | 'goc_giay'
  | 'giay_l'
  | 'thanh_v'
  | 'goc_v'
  | 'giay_tam'

export interface StructuredPackagingConversionResult {
  type_code: PackagingTypeCode | string
  params: Record<string, any>
  confidence: 'CONFIDENT' | 'NEEDS_REVIEW' | 'UNKNOWN'
  reason?: string
}

/**
 * Validates required parameters for a packaging type.
 */
export function validateStructuredPackagingParams(
  type_code: string,
  params: Record<string, any> = {},
): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  switch (type_code) {
    case 'thung': {
      if (!params.ply_count) errors.push('Thiếu số lớp (3/5)')
      if (!params.burst) errors.push('Thiếu độ bục LBS')
      if (parseN(params.length) <= 0) errors.push('Chiều dài (D) phải > 0')
      if (parseN(params.width) <= 0) errors.push('Chiều rộng (R) phải > 0')
      if (parseN(params.height) <= 0) errors.push('Chiều cao (C) phải > 0')
      if (!params.box_type) errors.push('Thiếu kiểu thùng (A1, A5...)')
      break
    }
    case 'xop_tam': {
      if (parseN(params.length) <= 0) errors.push('Chiều dài phải > 0')
      if (parseN(params.width) <= 0) errors.push('Chiều rộng phải > 0')
      if (parseN(params.thickness) <= 0) errors.push('Chiều dày phải > 0')
      if (!params.density) errors.push('Thiếu mật độ xốp')
      break
    }
    case 'xop_l': {
      if (parseN(params.length) <= 0) errors.push('Chiều dài phải > 0')
      if (!params.cross_section) errors.push('Thiếu tiết diện (vd: 90x90x20)')
      if (!params.density) errors.push('Thiếu mật độ')
      break
    }
    case 'xop_goc': {
      if (!params.spec) errors.push('Thiếu quy cách (vd: 90x90x90x15)')
      if (!params.density) errors.push('Thiếu mật độ')
      break
    }
    case 'goc_giay': {
      if (!params.spec || !['80x80x80', '100x100x100'].includes(String(params.spec))) {
        errors.push('Quy cách góc giấy phải là 80x80x80 hoặc 100x100x100')
      }
      break
    }
    case 'giay_l': {
      if (parseN(params.length) <= 0) errors.push('Chiều dài phải > 0')
      if (!params.cross_section) errors.push('Thiếu khổ giấy L')
      break
    }
    case 'thanh_v': {
      if (parseN(params.length) <= 0) errors.push('Chiều dài phải > 0')
      if (!params.thickness) errors.push('Thiếu độ dày (3 ly / 5 ly)')
      break
    }
    case 'goc_v': {
      if (!params.spec || typeof params.spec !== 'string') {
        errors.push('Thiếu quy cách (50*50*5)')
      }
      break
    }
    case 'giay_tam': {
      if (parseN(params.length) <= 0) errors.push('Chiều dài phải > 0')
      if (parseN(params.width) <= 0) errors.push('Chiều rộng phải > 0')
      break
    }
    default: {
      if (!type_code) errors.push('Chưa chọn loại bao bì')
      break
    }
  }

  return { isValid: errors.length === 0, errors }
}

/**
 * Calculates billable quantity and billable unit of measure from structured parameters.
 */
export function calculateStructuredBillableQty(
  type_code: string,
  params: Record<string, any>,
  orderQty: number,
): { billableQty: number; billableUom: string; orderUom: string; detailDesc: string } {
  const qty = parseN(orderQty) || 0

  switch (type_code) {
    case 'thung': {
      const D = parseN(params.length)
      const R = parseN(params.width)
      const C = parseN(params.height)
      const shape = String(params.box_type || '').trim()
      const dtM2PerBox = tinhDT_Carton(shape, D, R, C)
      const totalM2 = dtM2PerBox * qty
      return {
        billableQty: Number(totalM2.toFixed(4)),
        billableUom: 'm²',
        orderUom: 'thùng',
        detailDesc: `${dtM2PerBox.toFixed(4)} m²/thùng × ${qty} thùng = ${totalM2.toFixed(4)} m²`,
      }
    }
    case 'xop_tam': {
      const d = parseN(params.length)
      const r = parseN(params.width)
      const c = parseN(params.thickness)
      const vM3PerPiece = (d * r * c) / 1e9
      const totalM3 = vM3PerPiece * qty
      return {
        billableQty: Number(totalM3.toFixed(6)),
        billableUom: 'm³',
        orderUom: 'tấm',
        detailDesc: `${vM3PerPiece.toFixed(6)} m³/tấm × ${qty} tấm = ${totalM3.toFixed(4)} m³`,
      }
    }
    case 'xop_l': {
      const daiMm = parseN(params.length)
      const daiM = daiMm / 1000
      const totalM = daiM * qty
      return {
        billableQty: Number(totalM.toFixed(3)),
        billableUom: 'mét',
        orderUom: 'cái',
        detailDesc: `${daiM.toFixed(2)} m/thanh × ${qty} thanh = ${totalM.toFixed(2)} mét`,
      }
    }
    case 'xop_goc': {
      return {
        billableQty: qty,
        billableUom: 'cái',
        orderUom: 'cái',
        detailDesc: `${qty} cái`,
      }
    }
    case 'goc_giay': {
      return {
        billableQty: qty,
        billableUom: 'cái',
        orderUom: 'cái',
        detailDesc: `${qty} cái`,
      }
    }
    case 'giay_l': {
      const daiMm = parseN(params.length)
      const targetLabel = String(params.cross_section || '').trim()
      const opt = GIAY_L_OPTIONS.find((o) => o.label === targetLabel)
      let width = opt ? opt.width : 0
      if (!width && targetLabel) {
        if (targetLabel.includes('x')) {
          width = targetLabel.split('x').reduce((sum, part) => sum + parseN(part), 0)
        } else {
          width = parseN(targetLabel)
        }
      }
      const dtM2 = (daiMm * width) / 1e6
      const totalM2 = dtM2 * qty
      return {
        billableQty: Number(totalM2.toFixed(4)),
        billableUom: 'm²',
        orderUom: 'cái',
        detailDesc: `${dtM2.toFixed(4)} m²/thanh × ${qty} thanh = ${totalM2.toFixed(4)} m²`,
      }
    }
    case 'thanh_v': {
      const daiMm = parseN(params.length)
      const totalM = (daiMm / 1000) * qty
      return {
        billableQty: Number(totalM.toFixed(3)),
        billableUom: 'mét',
        orderUom: 'thanh',
        detailDesc: `${(daiMm / 1000).toFixed(2)} m/thanh × ${qty} thanh = ${totalM.toFixed(2)} mét`,
      }
    }
    case 'goc_v': {
      return {
        billableQty: qty,
        billableUom: 'cái',
        orderUom: 'cái',
        detailDesc: `${qty} cái`,
      }
    }
    case 'giay_tam': {
      const d = parseN(params.length)
      const r = parseN(params.width)
      const dtM2 = (d * r) / 1e6
      const totalM2 = dtM2 * qty
      return {
        billableQty: Number(totalM2.toFixed(4)),
        billableUom: 'm²',
        orderUom: 'tấm',
        detailDesc: `${dtM2.toFixed(4)} m²/tấm × ${qty} tấm = ${totalM2.toFixed(4)} m²`,
      }
    }
    default: {
      return {
        billableQty: qty,
        billableUom: 'cái',
        orderUom: 'cái',
        detailDesc: `${qty}`,
      }
    }
  }
}

/**
 * Converts a legacy packaging BOM line (loai, kt1..kt3, dinh_luong) into structured format.
 */
export function convertLegacyPackagingLineToStructured(
  line: {
    loai?: string | null
    kt1?: string | number | null
    kt2?: string | number | null
    kt3?: string | number | null
    dinh_luong?: string | number | null
    type_code?: string | null
    params?: Record<string, any> | null
  },
): StructuredPackagingConversionResult {
  if (line.type_code && line.params && Object.keys(line.params).length > 0) {
    const val = validateStructuredPackagingParams(line.type_code, line.params)
    return {
      type_code: line.type_code as PackagingTypeCode,
      params: line.params,
      confidence: val.isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: val.isValid ? undefined : val.errors.join('; '),
    }
  }

  const loaiRaw = String(line.loai || '').trim()
  const loaiLower = loaiRaw.toLowerCase()
  const dlStr = String(line.dinh_luong || '').trim()

  if (loaiLower.startsWith('thùng')) {
    const is3Lop = loaiLower.includes('3 lớp') || loaiLower.includes('3l')
    const ply_count = is3Lop ? 3 : 5
    let burst = '130 LBS'
    if (loaiLower.includes('175')) burst = '175 LBS'
    else if (loaiLower.includes('200')) burst = '200 LBS'
    else if (loaiLower.includes('275')) burst = '275 LBS'

    const length = parseN(line.kt1)
    const width = parseN(line.kt2)
    const height = parseN(line.kt3)
    const box_type = dlStr || 'Thùng A1'

    const params = { ply_count, burst, length, width, height, box_type }
    const isValid = length > 0 && width > 0 && height > 0
    return {
      type_code: 'thung',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Kích thước thùng chưa đầy đủ',
    }
  }

  if (loaiLower === 'xốp góc' || loaiLower === 'xop goc') {
    const spec = String(line.kt1 || '').trim()
    const density = dlStr || '16kg'
    const params = { spec, density }
    const isValid = Boolean(spec && density)
    return {
      type_code: 'xop_goc',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Thiếu quy cách hoặc mật độ xốp góc',
    }
  }

  if (loaiLower === 'xốp l' || loaiLower === 'xop l') {
    const length = parseN(line.kt1)
    const cross_section = String(line.kt2 || '').trim()
    const density = dlStr || '14kg'
    const params = { length, cross_section, density }
    const isValid = length > 0 && Boolean(cross_section) && Boolean(density)
    return {
      type_code: 'xop_l',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Thiếu chiều dài hoặc tiết diện xốp L',
    }
  }

  if (loaiLower === 'xốp' || loaiLower === 'xốp tấm' || loaiLower === 'xop' || loaiLower === 'xop tam') {
    const length = parseN(line.kt1)
    const width = parseN(line.kt2)
    const thickness = parseN(line.kt3)
    const density = dlStr || '12kg'
    const params = { length, width, thickness, density }
    const isValid = length > 0 && width > 0 && thickness > 0 && Boolean(density)
    return {
      type_code: 'xop_tam',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Kích thước hoặc mật độ xốp tấm chưa đủ',
    }
  }

  if (
    loaiLower === 'góc giấy 5 lớp' ||
    loaiLower === 'goc giay 5 lop' ||
    loaiLower.includes('8 5l') ||
    loaiLower.includes('10 5l') ||
    loaiLower.includes('góc 80') ||
    loaiLower.includes('góc 100')
  ) {
    let spec = '80x80x80'
    if (
      loaiLower.includes('10') ||
      loaiLower.includes('100') ||
      parseN(line.kt1) === 100
    ) {
      spec = '100x100x100'
    }
    return {
      type_code: 'goc_giay',
      params: { spec },
      confidence: 'CONFIDENT',
    }
  }

  if (loaiLower.startsWith('giấy l') || loaiLower.startsWith('giay l')) {
    const length = parseN(line.kt1)
    const cross_section = String(line.kt2 || '80x80').trim()
    const params = { length, cross_section }
    const isValid = length > 0 && Boolean(cross_section)
    return {
      type_code: 'giay_l',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Thiếu chiều dài hoặc khổ giấy L',
    }
  }

  if (loaiLower.startsWith('thanh v')) {
    const thickness = loaiLower.includes('5') ? '5 ly' : '3 ly'
    const length = parseN(line.kt1)
    const params = { thickness, length }
    const isValid = length > 0
    return {
      type_code: 'thanh_v',
      params,
      confidence: isValid ? 'CONFIDENT' : 'NEEDS_REVIEW',
      reason: isValid ? undefined : 'Thiếu chiều dài thanh V',
    }
  }

  if (loaiLower === 'góc v cứng' || loaiLower === 'goc v cung') {
    return {
      type_code: 'goc_v',
      params: { spec: '50*50*5' },
      confidence: 'CONFIDENT',
    }
  }

  if (loaiLower.startsWith('giấy tấm') || loaiLower.startsWith('giay tam')) {
    const length = parseN(line.kt1)
    const width = parseN(line.kt2)
    return {
      type_code: 'giay_tam',
      params: { length, width },
      confidence: length > 0 && width > 0 ? 'CONFIDENT' : 'NEEDS_REVIEW',
    }
  }

  return {
    type_code: loaiRaw,
    params: {},
    confidence: 'UNKNOWN',
    reason: `Chưa có quy tắc chuyển đổi cho loại '${loaiRaw}'`,
  }
}

/**
 * Converts structured packaging parameters back to legacy fields for backward compatibility.
 */
export function convertStructuredPackagingToLegacy(
  type_code: string,
  params: Record<string, any> = {},
): {
  loai: string
  kt1: any
  kt2: any
  kt3: any
  dinh_luong: any
  dvt: string
} {
  switch (type_code) {
    case 'thung': {
      const ply = params.ply_count ?? 5
      const burst = params.burst ?? '130 LBS'
      return {
        loai: `Thùng ${ply} lớp ${burst}`,
        kt1: params.length ?? null,
        kt2: params.width ?? null,
        kt3: params.height ?? null,
        dinh_luong: params.box_type ?? 'Thùng A1',
        dvt: 'thùng',
      }
    }
    case 'xop_tam': {
      return {
        loai: 'Xốp',
        kt1: params.length ?? null,
        kt2: params.width ?? null,
        kt3: params.thickness ?? null,
        dinh_luong: params.density ?? '12kg',
        dvt: 'm³',
      }
    }
    case 'xop_l': {
      return {
        loai: 'Xốp L',
        kt1: params.length ?? null,
        kt2: params.cross_section ?? '90x90x20',
        kt3: null,
        dinh_luong: params.density ?? '14kg',
        dvt: 'mét',
      }
    }
    case 'xop_goc': {
      return {
        loai: 'Xốp góc',
        kt1: params.spec ?? '90x90x90x15',
        kt2: null,
        kt3: null,
        dinh_luong: params.density ?? '16kg',
        dvt: 'cái',
      }
    }
    case 'goc_giay': {
      const is100 = String(params.spec || '').includes('100')
      return {
        loai: 'Góc giấy 5 lớp',
        kt1: is100 ? 100 : 80,
        kt2: is100 ? 100 : 80,
        kt3: is100 ? 100 : 80,
        dinh_luong: null,
        dvt: 'cái',
      }
    }
    case 'giay_l': {
      return {
        loai: 'Giấy L 5 lớp',
        kt1: params.length ?? null,
        kt2: params.cross_section ?? '80x80',
        kt3: null,
        dinh_luong: null,
        dvt: 'm²',
      }
    }
    case 'thanh_v': {
      const thickness = params.thickness ?? '3 ly'
      return {
        loai: `Thanh V cứng ${thickness}`,
        kt1: params.length ?? null,
        kt2: null,
        kt3: null,
        dinh_luong: null,
        dvt: 'mét',
      }
    }
    case 'goc_v': {
      return {
        loai: 'GÓC V CỨNG',
        kt1: params.spec ?? '50*50*5',
        kt2: null,
        kt3: null,
        dinh_luong: null,
        dvt: 'cái',
      }
    }
    case 'giay_tam': {
      return {
        loai: 'Giấy tấm 5L',
        kt1: params.length ?? null,
        kt2: params.width ?? null,
        kt3: null,
        dinh_luong: null,
        dvt: 'm²',
      }
    }
    default: {
      return {
        loai: type_code,
        kt1: null,
        kt2: null,
        kt3: null,
        dinh_luong: null,
        dvt: 'cái',
      }
    }
  }
}

/**
 * Formats a clean, standard specification string for materials and PO lines.
 */
export function formatStructuredPackagingSpec(
  type_code: string,
  params: Record<string, any> = {},
): string {
  switch (type_code) {
    case 'thung':
      return `${params.length || 0}x${params.width || 0}x${params.height || 0} - ${params.box_type || 'A1'} (${params.ply_count || 5} lớp ${params.burst || '130 LBS'})`
    case 'xop_tam':
      return `${params.length || 0}x${params.width || 0}x${params.thickness || 0} - ${params.density || '12kg'}`
    case 'xop_l':
      return `${params.length || 0}mm x ${params.cross_section || ''} - ${params.density || '14kg'}`
    case 'xop_goc':
      return `${params.spec || ''} - ${params.density || '16kg'}`
    case 'goc_giay':
      return `${params.spec || '80x80x80'}`
    case 'giay_l':
      return `${params.length || 0}mm x ${params.cross_section || '80x80'}`
    case 'thanh_v':
      return `${params.length || 0}mm x ${params.thickness || '3 ly'}`
    case 'goc_v':
      return `${params.spec || '50*50*5'}`
    case 'giay_tam':
      return `${params.length || 0}x${params.width || 0}`
    default:
      return Object.entries(params)
        .map(([k, v]) => `${k}:${v}`)
        .join(' - ')
  }
}
